import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import BackToTop from '../home/components/BackToTop';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0E0503] text-white py-6 sm:py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-[#E8C580] hover:text-white transition-colors mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Terms of Service</h1>
          <p className="text-[#E8C580] mt-2 text-sm sm:text-base">INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD</p>
          <p className="text-xs sm:text-sm text-gray-300 mt-1">ABN: 51 669 512 823 | ACN: 669 512 823</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-xs sm:text-sm text-gray-600 mb-6">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          {/* Cross-link */}
          <div className="flex items-center gap-3 bg-[#FFF9F5] border border-[#BA3D03]/30 rounded-lg px-4 py-3 mb-8">
            <i className="ri-shield-check-line text-[#BA3D03] w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
            <p className="text-sm text-[#3D1203] m-0">
              For information on how we handle your data, see our{' '}
              <Link to="/privacy-policy" className="text-[#BA3D03] font-medium hover:underline cursor-pointer">
                Privacy Policy &rarr;
              </Link>
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">1. Introduction</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Welcome to INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD (ABN: 51 669 512 823 | ACN: 669 512 823). These Terms of Service ("Terms") govern your access to and use of our training courses, website, and related services (collectively, the "Services").
            </p>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">2. Acceptance of Terms</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              By enrolling in any course, accessing our website, or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of the Services after any changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Course Enrolment */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">3. Course Enrolment and Participation</h2>
            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">3.1 Eligibility</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Our courses are designed for professionals in the NSW built environment sector. By enrolling, you confirm that you are at least 18 years of age and have the authority to enter into these Terms.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">3.2 Enrolment Process</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              To enrol in a course, you must complete the registration process and provide accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">3.3 Course Access</h3>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              Upon successful enrolment and payment, you will receive access to the course materials for the duration specified in your course package. Access may be revoked if you breach these Terms.
            </p>
          </section>

          {/* Payment & Refunds */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">4. Payment and Refunds</h2>
            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">4.1 Course Fees</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              All course fees are displayed in Australian Dollars (AUD) and include GST where applicable. Prices are subject to change without notice, but changes will not affect enrollments already confirmed.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">4.2 Payment Terms</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Payment is due at the time of enrolment unless otherwise agreed in writing. We accept payment via credit card, bank transfer, or other methods as specified during checkout.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">4.3 Refund Policy</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Refund requests must be submitted in writing to <a href="mailto:info@indigenousfacilitation.com.au" className="text-[#BA3D03] hover:underline">info@indigenousfacilitation.com.au</a>. Our refund policy is as follows:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] mb-4 text-sm sm:text-base">
              <li><strong>More than 14 days before course start:</strong> Full refund minus 10% administration fee</li>
              <li><strong>7-14 days before course start:</strong> 50% refund</li>
              <li><strong>Less than 7 days before course start:</strong> No refund</li>
              <li><strong>After course commencement:</strong> No refund</li>
            </ul>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              We reserve the right to cancel or reschedule courses. In such cases, you will receive a full refund or the option to transfer to an alternative date.
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">5. Intellectual Property</h2>
            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">5.1 Ownership</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              All course materials, including but not limited to text, graphics, videos, audio, software, and other content (collectively, "Content"), are the property of INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD or our licensors and are protected by Australian and international copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">5.2 Limited License</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Upon enrolment, you are granted a limited, non-exclusive, non-transferable license to access and use the course materials solely for your personal, non-commercial use. You may not:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] mb-4 text-sm sm:text-base">
              <li>Reproduce, distribute, modify, or create derivative works from the Content</li>
              <li>Share your login credentials or course access with others</li>
              <li>Record, screenshot, or otherwise copy course materials without written permission</li>
              <li>Use the Content for commercial purposes or resale</li>
            </ul>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">5.3 Cultural Sensitivity</h3>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              Our courses contain culturally sensitive information developed in consultation with Traditional Custodians. You agree to treat all course materials with respect and use them only in accordance with these Terms and the cultural protocols taught in the course.
            </p>
          </section>

          {/* User Conduct */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">6. User Conduct</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              You agree to use our Services in a manner that is lawful, respectful, and consistent with these Terms. You must not:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] text-sm sm:text-base">
              <li>Engage in any conduct that is discriminatory, harassing, or culturally insensitive</li>
              <li>Disrupt or interfere with the Services or other users' access to the Services</li>
              <li>Attempt to gain unauthorized access to any part of the Services</li>
              <li>Use the Services for any illegal or unauthorized purpose</li>
              <li>Transmit any viruses, malware, or other harmful code</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">7. Limitation of Liability</h2>
            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">7.1 Disclaimer</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Our courses are provided for educational purposes. While we strive to provide accurate and up-to-date information, we make no warranties or representations about the completeness, accuracy, or suitability of the course content for any particular purpose.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">7.2 Limitation</h3>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              To the maximum extent permitted by law, INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD, its directors, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Services, including but not limited to loss of profits, data, or business opportunities.
            </p>

            <h3 className="text-base sm:text-xl font-semibold text-[#0E0503] mb-2 sm:mb-3 mt-5 sm:mt-6">7.3 Maximum Liability</h3>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              Our total liability to you for any claims arising from your use of the Services shall not exceed the amount you paid for the course in question.
            </p>
          </section>

          {/* Indemnification */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">8. Indemnification</h2>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              You agree to indemnify, defend, and hold harmless INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to your breach of these Terms or your use of the Services.
            </p>
          </section>

          {/* Termination */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">9. Termination</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              We reserve the right to suspend or terminate your access to the Services at any time, with or without notice, for any reason, including but not limited to breach of these Terms. Upon termination, your right to use the Services will immediately cease.
            </p>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              Sections of these Terms that by their nature should survive termination (including but not limited to intellectual property, limitation of liability, and governing law provisions) shall survive termination.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">10. Governing Law and Jurisdiction</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              These Terms are governed by and construed in accordance with the laws of New South Wales, Australia. Any disputes arising out of or related to these Terms or the Services shall be subject to the exclusive jurisdiction of the courts of New South Wales.
            </p>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              Nothing in these Terms excludes, restricts, or modifies any consumer rights under the Australian Consumer Law or other applicable laws that cannot be excluded, restricted, or modified by agreement.
            </p>
          </section>

          {/* Severability */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">11. Severability</h2>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              If any provision of these Terms is found to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          {/* Entire Agreement */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">12. Entire Agreement</h2>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD regarding the use of our Services and supersede all prior agreements and understandings.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">13. Contact Us</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-[#FFF9F5] border-l-4 border-[#BA3D03] p-4 sm:p-6 rounded-lg">
              <p className="text-[#0E0503] font-semibold mb-2 text-sm sm:text-base">INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD</p>
              <p className="text-[#3D1203] mb-1 text-xs sm:text-sm">ABN: 51 669 512 823 | ACN: 669 512 823</p>
              <p className="text-[#3D1203] mb-1 text-xs sm:text-sm">
                <strong>Email:</strong> <a href="mailto:info@indigenousfacilitation.com.au" className="text-[#BA3D03] hover:underline">info@indigenousfacilitation.com.au</a>
              </p>
              <p className="text-[#3D1203] text-xs sm:text-sm">
                <strong>Location:</strong> Sydney, New South Wales, Australia
              </p>
            </div>
          </section>
        </div>
      </main>

      <BackToTop />
      <Footer />
    </div>
  );
}